import {
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  ViewChild,
  effect,
  ChangeDetectionStrategy,
} from '@angular/core'
import { Currency, Depot, File, Product, ProductDepot, PurchaseOrderItem } from 'depoto-core/src/entities'
import { FormRowTextareaComponent } from 'src/app/components/_common/form-row-textarea/form-row-textarea.component'

import { ModalContainerComponent } from 'src/app/components/_common/modal-container/modal-container.component'
import { CoreService } from 'src/app/services'
import { injectQuery } from '@tanstack/angular-query-experimental'
import { PaymentService } from '../../../../services/entity-services/payment.service'

@Component({
  selector: 'modal-stock-operation-card-purchase',
  templateUrl: 'modal-stock-operation-card-purchase.component.html',
  styleUrls: ['modal-stock-operation-card-purchase.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ModalStockOperationCardPurchaseComponent {
  private paymentService = inject(PaymentService)

  @ViewChild(ModalContainerComponent) public modal: ModalContainerComponent
  @ViewChild(FormRowTextareaComponent) public inputNoteComponent: FormRowTextareaComponent
  // public product: (Product | PurchaseOrderItemPlain) & { product: number }
  public product: Product
  @Input() public depots: Depot[] = []
  @Input() productDepot: ProductDepot
  @Input() depotQuantity: number
  @Input() isAddingNew = true
  @Output() public result: EventEmitter<any> = new EventEmitter()
  loading = false
  keyEventSubscriptions: any = {}
  @Input() editedItem: PurchaseOrderItem
  // orderItem: PurchaseOrderItemPlain
  orderItem: PurchaseOrderItem
  headerTitle: string

  readonly queryCurrencies = injectQuery(() => ({
    queryKey: ['currencies', 'options'],
    queryFn: async () => {
      return await this.paymentService.getCurrencies({}, { id: null, name: null, ratio: null }).then(result => {
        return result.items
      })
    },
  }))

  constructor(private core: CoreService) {
    this.initOrderItem()
    // When currencies load later, normalize model to the option object so the select shows the value
    effect(() => {
      const list = this.queryCurrencies.data()
      if (!list || !list.length) return
      this.normalizeCurrencyToOption()
    })
  }

  public addProduct(): void {
    this.loading = true
    this.result.emit(this.orderItem)
    this.hideChildModal()
  }

  showProductOnChildModal(): void {
    // Normalize currency to object because select uses bindToObject=true
    this.orderItem = { ...this.editedItem }
    this.normalizeCurrencyToOption(true)
    if (!this.orderItem.purchasePrice) this.orderItem.purchasePrice = 0
    this.showChildModal()
  }

  showProductOnChildModalForProductSelectio(item): void {
    // Ensure product is an object with fields needed by the template (fullName, code, ean, mainImage)
    const productObject =
      typeof item?.product === 'object'
        ? item.product
        : {
            id: item?.product,
            fullName: (item as any)?.fullName,
            mainImage: (item as any)?.mainImage,
            code: (item as any)?.code,
            ean: (item as any)?.ean,
          }
    this.orderItem = { ...item, product: productObject }
    this.normalizeCurrencyToOption(true)

    if (!this.orderItem.purchasePrice) this.orderItem.purchasePrice = 0
    this.showChildModal()
  }

  public showChildModal(): void {
    this.keyEventSubscriptions.keyEsc = this.core.services.keyEvent.onKeyEsc.subscribe().then(() => {
      this.modal.hideChildModal()
    })
    this.modal.showChildModal()
  }

  public hideChildModal(): void {
    if (this.keyEventSubscriptions.keyEsc) {
      this.keyEventSubscriptions.keyEsc.unsubscribe()
      this.keyEventSubscriptions.keyEsc = null
    }
    this.product = null
    this.initOrderItem()
    this.modal.hideChildModal()
    this.loading = false
  }

  private initOrderItem(): void {
    this.orderItem = new PurchaseOrderItem()
    // Prefill default currency to company currency (will be normalized to option once loaded)
    const companyCurrency = this.core?.services?.user?.user?.company?.currency
    if (companyCurrency?.id) {
      // set as object with id so later normalization can resolve to the option object
      ;(this.orderItem as any).currency = { id: companyCurrency.id, name: companyCurrency.name } as any
    }
  }

  currencyChange(currency) {
    const selected = this.queryCurrencies.data()?.find(c => String(c.id) === String(currency?.id)) || null
    this.orderItem.currency = selected as any
  }

  // Ensure orderItem.currency is the option object from queryCurrencies when possible.
  // If missing, default to company currency.
  private normalizeCurrencyToOption(applyDefaultIfMissing: boolean = false): void {
    const list = this.queryCurrencies.data()
    const current = this.orderItem?.currency as any
    const currentId = typeof current === 'string' ? current : current?.id

    let desiredId = currentId
    if (!desiredId && applyDefaultIfMissing) {
      const companyCurrency = this.core?.services?.user?.user?.company?.currency
      desiredId = companyCurrency?.id
    }

    // todo check
    if (!desiredId) return

    if (Array.isArray(list) && list.length) {
      const found = list.find(c => String(c.id) === String(desiredId))
      if (found && current !== found) {
        this.orderItem.currency = found as any
        return
      }
    }
    // Fallback: keep minimal object with id so UI can still reflect a selection
    if (!current || (typeof current === 'string' && String(current) !== String(desiredId))) {
      this.orderItem.currency = { id: desiredId } as any
    }
  }

  currencyChanged(event) {
    // Support both shapes emitted by form-row-select: option object or primitive id
    const list = this.queryCurrencies.data?.() || []
    if (event == null) {
      this.orderItem.currency = null as any
      return
    }
    const id = typeof event === 'object' ? (event as any)?.id : event
    const found = Array.isArray(list) ? list.find(c => String((c as any)?.id) === String(id)) : null
    // If bindToObject=true and event is the option object, assign it; otherwise use the found option or minimal object with id
    this.orderItem.currency = typeof event === 'object' ? (event as any) : (found as any) ?? ({ id } as any)
  }
}
